import { IUser } from 'Models/User';
import { UserRoles } from 'Enums/UserRoles';
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { TypeOf, any, nativeEnum, object, string, union, instanceof as zInstanceof } from 'zod';
import { ISelectOption } from 'Interfaces/Utils';

// Form component interface

export interface IUserFormProps {
  currentUser?: IUser;
  companies: ISelectOption<string>[];
  createApiError: FetchBaseQueryError | SerializedError | undefined;
  editApiError?: FetchBaseQueryError | SerializedError | undefined;
  onSubmit: (formData: FormData) => void;
};

// Static vars

export const ROLE_OPTIONS = [
  {
    label: UserRoles.ADMIN,
    value: "userRoles.ADMIN",
  },
  {
    label: UserRoles.SUPERADMIN,
    value: "userRoles.SUPERADMIN",
  },
  {
    label: UserRoles.MANAGER,
    value: "userRoles.MANAGER",
  },
  {
    label: UserRoles.TECHNICIAN,
    value: "userRoles.TECHNICIAN",
  }
  
];

// Form fields

export const FIELDS = {
  IMAGE: {
    FIELD: 'image',
  },
  ID: {
    FIELD: 'id',
  },
  NAME: {
    FIELD: 'name',
    LABEL: 'user.nameOnly',
    ERROR: 'El nombre es obligatorio'
  },
  LASTNAME: {
    FIELD: 'lastName',
    LABEL: 'user.lastName',
    ERROR: 'El apellido es obligatorio'
  },
  EMAIL: {
    FIELD: 'email',
    LABEL: 'user.email',
    ERROR_REQUIRED: 'El email es obligatorio',
    ERROR_INVALID: 'Email inválido'
  },

  ROLE: {
    FIELD: 'role',
    LABEL: 'user.role',
  },
  COMPANY: {
    FIELD: 'companyId',
    LABEL: 'user.company',
    ERROR: 'La empresa es obligatoria'
  },
  POINT_OF_SALE: {
    FIELD: 'pointOfSaleId',
    LABEL: 'user.pointOfSale',
    ERROR: 'El punto de venta es obligatorio'
  },
};

// Form validation schema

export const userFormSchema = object({
  [FIELDS.ID.FIELD]: string().optional(),
  [FIELDS.NAME.FIELD]: string().min(1, FIELDS.NAME.ERROR),
  [FIELDS.LASTNAME.FIELD]: string().min(1, FIELDS.NAME.ERROR),
  [FIELDS.EMAIL.FIELD]: string().min(1, FIELDS.EMAIL.ERROR_REQUIRED).email(FIELDS.EMAIL.ERROR_INVALID),
  [FIELDS.ROLE.FIELD]: nativeEnum(UserRoles),
  [FIELDS.IMAGE.FIELD]: union([string(), zInstanceof(File)]).optional(),
  [FIELDS.COMPANY.FIELD]: string().optional(), // Inicialmente opcional
  [FIELDS.POINT_OF_SALE.FIELD]: string().optional(), // Inicialmente opcional

  general: any()

})
// Validación para asegurarse de que la empresa está seleccionada para roles distintos a SUPERADMIN
.refine((data) => {
  if (data[FIELDS.ROLE.FIELD] !== UserRoles.SUPERADMIN && !data[FIELDS.COMPANY.FIELD]) {
    return false;
  }
  return true;
}, {
  message: FIELDS.COMPANY.ERROR,
  path: [FIELDS.COMPANY.FIELD],
})
// Validación para asegurarse de que el punto de venta está seleccionado para roles específicos
.refine((data) => {
  if (
    data[FIELDS.ROLE.FIELD] !== UserRoles.SUPERADMIN &&
    data[FIELDS.ROLE.FIELD] !== UserRoles.ADMIN &&
    data[FIELDS.ROLE.FIELD] !== UserRoles.MANAGER &&
    !data[FIELDS.POINT_OF_SALE.FIELD]
  ) {
    return false;
  }
  return true;
}, {
  message: FIELDS.POINT_OF_SALE.ERROR, // Agrega un mensaje de error específico para este caso
  path: [FIELDS.POINT_OF_SALE.FIELD],
})
// Validación para asegurarse de que si se selecciona un punto de venta, también se selecciona una empresa
.refine((data) => {
  if (data[FIELDS.POINT_OF_SALE.FIELD] && !data[FIELDS.COMPANY.FIELD]) {
    return false;
  }
  return true;
}, {
  message: FIELDS.COMPANY.ERROR,
  path: [FIELDS.COMPANY.FIELD],
});

// Form values interface

export type IUserForm = TypeOf<typeof userFormSchema>;

// Default values function

export const initialValues = (currentUser: IUser | undefined, user:IUser| undefined): IUserForm => {
  const userForm: IUserForm = {
    name: currentUser?.name || "",
    lastName: currentUser?.lastName || "",
    role: currentUser?.role || "",
    email: currentUser?.email || "",
    image: currentUser?.image || "",
    id: currentUser?.id || "",
    companyId: currentUser?.companyId || user?.companyId,
    pointOfSaleId: currentUser?.pointOfSaleId || "",
  };

  return userForm;
};

// Translations

export const EMAIL_SEND_TOAST = "Se ha enviado el mensaje de activación de la cuenta del usuario";
export const EMAIL_ERROR_TOAST = "Ha ocurrido un error en el envío del mensaje de activación de la cuenta del usuario";